import { RendererProps, getBoolPath, noop, resolveDynamicBool, resolveDynamicString } from 'components/a2ui/types'
import { RenderField } from 'components/core/dynamic-form/fields/RenderField'
import { getByPointer } from 'lib/a2ui/resolver'
import { Field } from 'pbts/c1/api/form/v1/form'
import { ChangeEvent, FC, useCallback, useMemo } from 'react'

type CheckBoxRendererProps = Pick<RendererProps, 'component' | 'dataModel' | 'onDataChange' | 'disabled'>

/**
 * Renders a checkbox field using C1's RenderField component.
 * Maps the A2UI CheckBox component to a Field proto structure with boolField type.
 */
export const CheckBoxRenderer: FC<CheckBoxRendererProps> = ({
    component,
    dataModel,
    onDataChange,
    disabled = false,
}) => {
    const cb = component.component.oneofKind === 'checkBox' ? component.component.checkBox : undefined
    const label = useMemo(
        () => (cb ? resolveDynamicString(cb.label, dataModel, getByPointer) : ''),
        [cb, dataModel],
    )
    const path = cb ? getBoolPath(cb.value) : undefined
    const checked = path
        ? Boolean(getByPointer(dataModel, path))
        : cb ? resolveDynamicBool(cb.value, dataModel, getByPointer) : false

    const field: Field = useMemo(() => ({
        name: component.id,
        displayName: label,
        description: '',
        required: false,
        type: {
            oneofKind: 'boolField',
            boolField: {
                defaultValue: false,
                view: {
                    oneofKind: 'checkboxField',
                    checkboxField: {},
                },
            },
        },
        providerConfig: { oneofKind: undefined },
    }), [component.id, label])

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
        if (path) onDataChange(path, e.target.checked)
    }, [path, onDataChange])

    const cField = useMemo(() => ({
        value: checked,
        onChange: handleChange,
        onBlur: noop,
        name: component.id,
        ref: noop,
    }), [checked, handleChange, component.id])

    if (!cb) return null

    return <RenderField field={field} cField={cField} errors={{}} disabled={disabled} />
}
